/**
 * Performance chart with dark theme styling.
 */
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { clsx } from 'clsx';
import { formatCurrency, formatDate } from '@/utils/formatters';

export interface PerformanceDataPoint {
  date: string;
  value: number;
}

interface PerformanceChartProps {
  data: PerformanceDataPoint[];
  isLoading?: boolean;
  className?: string;
}

export function PerformanceChart({
  data,
  isLoading = false,
  className,
}: PerformanceChartProps) {
  const percentChange =
    data.length >= 2
      ? ((data[data.length - 1]!.value - data[0]!.value) / data[0]!.value) * 100
      : 0;

  const isPositive = percentChange >= 0;
  const lineColor = isPositive ? '#10b981' : '#f43f5e';
  const gradientId = isPositive ? 'greenGradient' : 'redGradient';

  // Compute a tight Y-axis domain with padding so trends are visible
  const values = data.map((d) => d.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;
  const padding = range > 0 ? range * 0.15 : maxValue * 0.02;
  const yDomain: [number, number] = [
    Math.max(0, Math.floor((minValue - padding) / 1000) * 1000),
    Math.ceil((maxValue + padding) / 1000) * 1000,
  ];

  if (isLoading) {
    return (
      <div className={clsx('space-y-4', className)}>
        <div className="h-6 w-32 rounded shimmer" />
        <div className="h-64 rounded shimmer" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-dark-700 bg-dark-800/50">
        <p className="text-sm text-dark-500">No performance data available</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="h-64" role="img" aria-label="Portfolio performance chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <defs>
              <linearGradient id="greenGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="redGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#f43f5e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => formatDate(value)}
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={{ stroke: '#334155' }}
            />
            <YAxis
              domain={yDomain}
              tickFormatter={(value) => formatCurrency(value)}
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={false}
              width={80}
            />
            <Tooltip
              formatter={(value: number) => [formatCurrency(value), 'Value']}
              labelFormatter={(label) => formatDate(label)}
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.3)',
              }}
              labelStyle={{ color: '#94a3b8' }}
              itemStyle={{ color: '#f1f5f9' }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={lineColor}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4, fill: lineColor, stroke: '#0f172a', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Accessible data table */}
      <table className="sr-only">
        <caption>Portfolio performance data</caption>
        <thead>
          <tr>
            <th>Date</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {data.map((point) => (
            <tr key={point.date}>
              <td>{formatDate(point.date)}</td>
              <td>{formatCurrency(point.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
